import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Award,
  Target,
  TrendingUp,
  Calendar,
  Shield,
  HeartHandshake,
  Code2,
  Users,
  Cpu,
} from "lucide-react";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_b7ec6732-ddda-4855-9f74-a08614294f29/artifacts/3skf9vwq_Humera%20Rindani%20-%20Pic%20in%20Hijab.jpeg";
const HERO_BG =
  "https://images.pexels.com/photos/7703038/pexels-photo-7703038.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600";
const PACKAGING =
  "https://images.pexels.com/photos/36339062/pexels-photo-36339062.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=850&w=1200";

// ─────────── Slide layout primitives ───────────
function Slide({ children, theme = "ivory", number, total, label, isPdf }) {
  const bg = theme === "onyx" ? "var(--onyx)" : "var(--ivory)";
  const fg = theme === "onyx" ? "var(--ivory)" : "var(--ink)";
  return (
    <section
      className={`relative w-full mx-auto print-slide ${isPdf ? "" : "grain"}`}
      style={{
        width: "100%",
        minHeight: isPdf ? "auto" : "100vh",
        height: isPdf ? "210mm" : "auto",
        maxWidth: isPdf ? "297mm" : "1280px",
        background: bg,
        color: fg,
        padding: isPdf ? "20mm 22mm" : "48px 64px",
        pageBreakAfter: "always",
        breakAfter: "page",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-7 h-7 flex items-center justify-center"
            style={{ border: `1px solid ${theme === "onyx" ? "var(--champagne)" : "var(--bronze)"}` }}
          >
            <span
              className="font-serif-display"
              style={{ fontSize: 14, color: theme === "onyx" ? "var(--champagne)" : "var(--bronze)" }}
            >
              B
            </span>
          </div>
          <div className="leading-tight">
            <div className="font-serif-display text-[14px] tracking-tight" style={{ color: fg }}>
              BEAUTWELLZ
            </div>
            <div className="overline" style={{ fontSize: 9, color: theme === "onyx" ? "var(--champagne)" : "var(--bronze)" }}>
              Investor Brief · 2026
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="overline" style={{ fontSize: 10, color: theme === "onyx" ? "var(--champagne)" : "var(--bronze)" }}>
            {label}
          </div>
          <div className="font-mono-data text-[12px]" style={{ color: theme === "onyx" ? "rgba(247,245,240,0.6)" : "var(--ink-2)" }}>
            {String(number).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
        </div>
      </div>
      <div style={{ height: 1, background: theme === "onyx" ? "rgba(247,245,240,0.12)" : "rgba(26,26,26,0.1)" }} />
      <div className="mt-6 relative z-10">{children}</div>
    </section>
  );
}

// ─────────── Individual slides ───────────
function SlideHero({ number, total, isPdf }) {
  return (
    <Slide number={number} total={total} label="Cover" theme="ivory" isPdf={isPdf}>
      {!isPdf && (
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center", filter: "saturate(0.7)" }}
        />
      )}
      <div className="relative grid grid-cols-12 gap-10 items-center" style={{ minHeight: isPdf ? "150mm" : "80vh" }}>
        <div className="col-span-7">
          <div className="overline mb-6">Founder &amp; Director · BEAUTWELLZ OPC PVT LTD</div>
          <h1 className="font-serif-display text-[78px] leading-[0.95] tracking-tight" style={{ color: "var(--ink)" }}>
            Humera
            <br />
            <em style={{ color: "var(--bronze)" }}>Rindani.</em>
          </h1>
          <p className="mt-6 max-w-xl text-[15px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
            17 yrs Cosmetics &amp; Haircare R&amp;D · 6 years of solo bootstrapping ·
            ₹1Cr+ cumulative revenue FY 2020–26 · Mumbai HQ + Hyderabad expansion ·
            Attar D2C on the runway.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-5 max-w-xl">
            {[
              { v: "₹1Cr+", l: "FY 2020-26 Cumulative" },
              { v: "17 yrs", l: "R&D Experience" },
              { v: "4.3/5", l: "Verified Rating" },
            ].map((k) => (
              <div key={k.l}>
                <div className="metric-value text-[38px]" style={{ color: "var(--ink)" }}>{k.v}</div>
                <div className="overline mt-1" style={{ fontSize: 9, color: "var(--ink-2)" }}>{k.l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-5">
          <div className="relative aspect-[4/5] w-full overflow-hidden" style={{ border: "1px solid rgba(26,26,26,0.12)" }}>
            <img src={FOUNDER_IMG} alt="Humera Rindani" className="w-full h-full object-cover" />
            <div className="absolute left-0 bottom-0 right-0 px-4 py-3" style={{ background: "rgba(247,245,240,0.82)" }}>
              <div className="overline" style={{ fontSize: 9 }}>Founder · Director · R&amp;D</div>
              <div className="font-serif-display text-[16px]" style={{ color: "var(--ink)" }}>
                Glass · Packaging · Private Label · Attar
              </div>
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

function SlideFounder({ number, total, isPdf }) {
  const facts = [
    { k: "Registered Office", v: "Mumbai · Maharashtra" },
    { k: "Warehouse", v: "Vasai · Palghar · MH" },
    { k: "Operating Office", v: "Tolichowki · Hyderabad (TG)" },
    { k: "GST", v: "Maharashtra + Telangana" },
    { k: "R&D Depth", v: "17 yrs · Cosmetics + Hair" },
    { k: "Capital Stack", v: "Bootstrapped · Profitable" },
  ];
  return (
    <Slide number={number} total={total} label="Founder" theme="ivory" isPdf={isPdf}>
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-5">
          <div className="overline mb-4">The Operator</div>
          <h2 className="font-serif-display text-[52px] leading-[1.02]" style={{ color: "var(--ink)" }}>
            17 years of R&amp;D. <em style={{ color: "var(--bronze)" }}>Six years</em> of solo bootstrapping.
          </h2>
          <p className="mt-6 text-[14px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
            Founder &amp; Director of BEAUTWELLZ OPC PVT LTD — registered in Mumbai
            with operational warehouse at Vasai, Palghar. ₹1Cr+ cumulative revenue
            across FY 2020-26, solo and bootstrapped.
          </p>
          <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
            17 years of corporate Cosmetics &amp; Haircare R&amp;D — a defensible advisory
            moat over generic packaging distributors. Now operating from a new
            Tolichowki, Hyderabad office (Jan 2026) on the freshly acquired
            Telangana GST.
          </p>
        </div>
        <div className="col-span-7 grid grid-cols-2 gap-3 content-start">
          {facts.map((r) => (
            <div key={r.k} className="card-flat p-4">
              <div className="overline" style={{ fontSize: 10 }}>{r.k}</div>
              <div className="font-mono-data mt-1 text-[14px]" style={{ color: "var(--ink)" }}>{r.v}</div>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function SlideValuation({ number, total, isPdf }) {
  return (
    <Slide number={number} total={total} label="Investment Opportunity" theme="onyx" isPdf={isPdf}>
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-7">
          <div className="overline" style={{ color: "var(--champagne)" }}>Investment Opportunity</div>
          <h2 className="font-serif-display text-[68px] leading-[0.98] mt-4" style={{ color: "var(--ivory)" }}>
            Valuation<br /><em style={{ color: "var(--champagne)" }}>₹1.5 — 4 Crore.</em>
          </h2>
          <p className="mt-6 max-w-xl text-[14px]" style={{ color: "rgba(247,245,240,0.7)" }}>
            Indicative range based on 3–5× EBITDA. Premium justified by 6 years of
            profitable bootstrapping, R&amp;D-led private-label moat, dual-state
            operating footprint and Attar D2C optionality.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { v: "₹1 Cr", l: "Ask" },
              { v: "15-20%", l: "Stake" },
              { v: "8-10 mo", l: "Payback" },
            ].map((k) => (
              <div key={k.l} className="p-4" style={{ border: "1px solid rgba(212,175,55,0.4)" }}>
                <div className="overline" style={{ color: "var(--champagne)", fontSize: 9 }}>{k.l}</div>
                <div className="metric-value text-[32px]" style={{ color: "var(--ivory)" }}>{k.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-5">
          <div className="overline mb-4" style={{ color: "var(--champagne)" }}>Use of Funds</div>
          <div className="space-y-4">
            {[
              { k: "Inventory & Working Capital", v: 30 },
              { k: "Attar D2C Brand Launch", v: 25 },
              { k: "Digital, SEO & Brand", v: 20 },
              { k: "Team & Operations", v: 15 },
              { k: "Compliance & Reserve", v: 10 },
            ].map((r) => (
              <div key={r.k}>
                <div className="flex justify-between text-[12px]" style={{ color: "rgba(247,245,240,0.85)" }}>
                  <span>{r.k}</span>
                  <span className="font-mono-data" style={{ color: "var(--champagne)" }}>{r.v}%</span>
                </div>
                <div className="h-[5px] mt-1.5" style={{ background: "rgba(247,245,240,0.1)" }}>
                  <div className="h-full" style={{ width: `${r.v}%`, background: "var(--champagne)" }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 p-4" style={{ border: "1px solid rgba(247,245,240,0.18)" }}>
            <div className="overline" style={{ color: "var(--champagne)", fontSize: 9 }}>EBITDA Multiple Logic</div>
            <div className="font-mono-data text-[13px] mt-2" style={{ color: "rgba(247,245,240,0.85)" }}>
              3× EBITDA → ₹1.5 Cr · 5× EBITDA → ₹4.0 Cr
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

function SlideHighlights({ number, total, isPdf }) {
  const h = [
    { k: "Valuation Range", v: "₹1.5 – 4 Cr", n: "3-5× EBITDA multiple", i: <Award size={18} /> },
    { k: "Revenue Impact", v: "₹45 – 55 L", n: "Year-1 digital initiatives", i: <TrendingUp size={18} /> },
    { k: "Market Growth", v: "12 – 25%", n: "CAGR across segments", i: <Target size={18} /> },
    { k: "ROI Payback", v: "8 – 10 mo", n: "Full digital investment", i: <Calendar size={18} /> },
  ];
  return (
    <Slide number={number} total={total} label="Key Highlights" theme="ivory" isPdf={isPdf}>
      <div className="overline mb-3">Key Investment Highlights</div>
      <h2 className="font-serif-display text-[52px] leading-[1.02] mb-10" style={{ color: "var(--ink)" }}>
        Four numbers an investor needs to <em style={{ color: "var(--bronze)" }}>remember.</em>
      </h2>
      <div className="grid grid-cols-2 gap-6">
        {h.map((x) => (
          <div key={x.k} className="card-flat p-8">
            <div className="flex items-center gap-3" style={{ color: "var(--bronze)" }}>
              {x.i}
              <div className="overline">{x.k}</div>
            </div>
            <div className="metric-value text-[64px] mt-4" style={{ color: "var(--ink)" }}>{x.v}</div>
            <div className="text-[13px] mt-2" style={{ color: "var(--ink-2)" }}>{x.n}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 p-5" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(156,124,56,0.3)" }}>
        <div className="overline" style={{ fontSize: 10 }}>Outbound Channels</div>
        <div className="text-[14px] mt-1" style={{ color: "var(--ink-2)" }}>
          Listed on <strong style={{ color: "var(--ink)" }}>OpenVC.com</strong> · application-ready for{" "}
          <strong style={{ color: "var(--ink)" }}>UAE Make-it-in-the-Emirates &amp; SME grants</strong>.
        </div>
      </div>
    </Slide>
  );
}

function SlideRoadmap({ number, total, isPdf }) {
  const quarters = [
    { q: "Q1", l: "Foundation", items: ["Website rebuild", "GMB (Mumbai+Hyd)", "Cornerstone content", "LinkedIn page", "Attar D2C identity"] },
    { q: "Q2", l: "Launch", items: ["Attar D2C live", "Instagram reels grid", "Programmatic SEO", "Webinar #1", "Vendor QC SOP"] },
    { q: "Q3", l: "Compounding", items: ["30k monthly visits", "UAE grant submitted", "GCC outbound", "Indie Beauty paper", "Inside Sales hire"] },
    { q: "Q4", l: "Scale", items: ["₹45-55L impact", "OpenVC convos", "Studio CapEx", "Series A readiness", "Year-2 board plan"] },
  ];
  return (
    <Slide number={number} total={total} label="12-Month Roadmap" theme="ivory" isPdf={isPdf}>
      <div className="overline mb-3">12-Month Implementation</div>
      <h2 className="font-serif-display text-[46px] leading-tight mb-10" style={{ color: "var(--ink)" }}>
        From cottage cadence to <em style={{ color: "var(--bronze)" }}>boardroom-ready</em> operating rhythm.
      </h2>
      <div className="grid grid-cols-4 gap-5">
        {quarters.map((q, i) => (
          <div key={q.q} className={`timeline-step ${i === quarters.length - 1 ? "last" : ""}`}>
            <div className="font-serif-display text-[28px]" style={{ color: "var(--ink)" }}>{q.q}</div>
            <div className="overline mt-1" style={{ fontSize: 10 }}>{q.l}</div>
            <ul className="mt-3 space-y-2">
              {q.items.map((it) => (
                <li key={it} className="flex items-start gap-2 text-[12px]" style={{ color: "var(--ink-2)" }}>
                  <span className="divider-dot mt-2" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Slide>
  );
}

function SlideRisk({ number, total, isPdf }) {
  const risks = [
    { r: "Quality control variability on bulk dispatches", i: "High", p: "Medium", m: "Tiered vendor QC SOP + pre-shipment inspection." },
    { r: "Billing & reconciliation disputes", i: "Medium", p: "Medium", m: "Zoho Books / Tally Prime + e-invoicing." },
    { r: "Single-founder key-person risk", i: "High", p: "Medium", m: "Q3 hire of Inside Sales + Brand Ops · SOPs documented." },
    { r: "Vendor lead-time volatility", i: "Medium", p: "High", m: "Dual-source top 20 SKUs · safety stock · SLAs." },
    { r: "Buyer concentration risk", i: "Medium", p: "Medium", m: "Cap top-buyer share at 18% · diversify to GCC + Tier-2." },
    { r: "Attar D2C pre-revenue execution", i: "Medium", p: "Medium", m: "Phased D2C → Amazon → GCC over 6 months." },
  ];
  const pill = (lvl) => {
    const cls = lvl === "High" ? "pill pill-high" : lvl === "Medium" ? "pill pill-med" : "pill pill-low";
    return <span className={cls}>{lvl}</span>;
  };
  return (
    <Slide number={number} total={total} label="Risk & Mitigation" theme="ivory" isPdf={isPdf}>
      <div className="overline mb-3">Risk Assessment &amp; Mitigation</div>
      <h2 className="font-serif-display text-[46px] leading-tight mb-8" style={{ color: "var(--ink)" }}>
        Six risks. <em style={{ color: "var(--bronze)" }}>Six mitigations.</em>
      </h2>
      <div className="card-flat overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "var(--ink-2)", fontFamily: "Outfit" }}>
              <th className="py-3 px-4">Risk</th>
              <th className="py-3 px-4">Impact</th>
              <th className="py-3 px-4">Probability</th>
              <th className="py-3 px-4">Mitigation</th>
            </tr>
          </thead>
          <tbody>
            {risks.map((r) => (
              <tr key={r.r} style={{ borderTop: "1px solid rgba(26,26,26,0.08)" }}>
                <td className="py-3 px-4 font-serif-display text-[14px]" style={{ color: "var(--ink)" }}>{r.r}</td>
                <td className="py-3 px-4">{pill(r.i)}</td>
                <td className="py-3 px-4">{pill(r.p)}</td>
                <td className="py-3 px-4 text-[12px]" style={{ color: "var(--ink-2)" }}>{r.m}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Slide>
  );
}

function SlideClose({ number, total, isPdf }) {
  return (
    <Slide number={number} total={total} label="Connect" theme="onyx" isPdf={isPdf}>
      {!isPdf && (
        <div
          className="absolute inset-0 opacity-[0.1] pointer-events-none"
          style={{ backgroundImage: `url(${PACKAGING})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
      )}
      <div className="relative grid grid-cols-12 gap-10 items-center" style={{ minHeight: isPdf ? "140mm" : "70vh" }}>
        <div className="col-span-7">
          <div className="overline" style={{ color: "var(--champagne)" }}>Let&apos;s talk numbers</div>
          <h2 className="font-serif-display text-[78px] leading-[0.95] mt-4" style={{ color: "var(--ivory)" }}>
            Thank you.<br />
            <em style={{ color: "var(--champagne)" }}>Your move.</em>
          </h2>
          <p className="mt-6 text-[14px]" style={{ color: "rgba(247,245,240,0.7)", maxWidth: 520 }}>
            For investor decks, banker NDAs, term sheets or strategic partnerships —
            reach Humera directly. All conversations held in confidence.
          </p>
          <div className="mt-10 space-y-3 text-[14px]" style={{ color: "var(--ivory)" }}>
            <div>📧 humera.rindani@beautywellness.in</div>
            <div>🔗 LinkedIn · /in/humerarindani</div>
            <div>📍 Mumbai · Vasai · Hyderabad · India</div>
          </div>
        </div>
        <div className="col-span-5">
          <div className="p-7" style={{ border: "1px solid rgba(212,175,55,0.4)", background: "rgba(212,175,55,0.06)" }}>
            <div className="overline mb-3" style={{ color: "var(--champagne)" }}>Channels</div>
            <ul className="space-y-3 text-[13px]" style={{ color: "rgba(247,245,240,0.85)" }}>
              <li>· OpenVC.com (live listing)</li>
              <li>· UAE Make-it-in-the-Emirates application</li>
              <li>· Direct outreach via this brief</li>
            </ul>
            <div className="mt-6 pt-5" style={{ borderTop: "1px solid rgba(247,245,240,0.15)" }}>
              <Shield size={14} style={{ color: "var(--champagne)" }} />
              <div className="text-[11px] mt-2" style={{ color: "rgba(247,245,240,0.5)", fontFamily: "Outfit", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Confidential · Prepared for investors &amp; bankers
              </div>
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

function SlideJourney({ number, total, isPdf }) {
  const chapters = [
    { n: "Pre-2018", t: "Haircare R&D · India", d: "17 yrs corporate R&D — Mumbai-based, live product demonstrations across Delhi, Kolkata, Mumbai stages.", i: <HeartHandshake size={14} /> },
    { n: "2018-19", t: "The Vision · From a Sickbed", d: "Recovering from chronic illness, Humera predicted India's hygiene-economy reset. Seeded medical disposables. Investors said no. She replied: I'll do it myself.", i: <HeartHandshake size={14} /> },
    { n: "Dec'19 - Feb'20", t: "The Leap", d: "Left corporate role Dec 2019. Incorporated BEAUTWELLZ OPC and integrated the vision by Feb 2020 — weeks before lockdown.", i: <Code2 size={14} /> },
    { n: "2020-21", t: "Vision Validated", d: "COVID hit. Medical disposables became national-priority inventory. The vision dismissed in 2018 funded BEAUTWELLZ's first crores.", i: <Users size={14} /> },
    { n: "2022-24", t: "Self-Taught Pivot", d: "Built site herself. Pivoted to higher-margin glass perfume bottles, cosmetic packaging & private-label execution.", i: <Code2 size={14} /> },
    { n: "2025-26", t: "AI-First Founder", d: "Hyderabad relocation. AI-agent dashboard runs quoting, content, follow-ups, analytics — 10× output, 2-person team.", i: <Cpu size={14} /> },
  ];
  return (
    <Slide number={number} total={total} label="Founder's Journey" theme="ivory" isPdf={isPdf}>
      <div className="overline mb-3">The Journey · 2018 → 2026</div>
      <h2 className="font-serif-display text-[42px] leading-[1.02] mb-6" style={{ color: "var(--ink)" }}>
        A founder who saw the pandemic <em style={{ color: "var(--bronze)" }}>two years before</em> it arrived.
      </h2>
      <div className="grid grid-cols-3 gap-4">
        {chapters.map((c) => (
          <div key={c.n} className="card-flat p-4">
            <div className="flex items-center gap-2 mb-1.5" style={{ color: "var(--bronze)" }}>
              {c.i}<div className="overline" style={{ fontSize: 9 }}>{c.n}</div>
            </div>
            <div className="font-serif-display text-[17px] leading-tight" style={{ color: "var(--ink)" }}>{c.t}</div>
            <p className="mt-1.5 text-[11px] leading-relaxed" style={{ color: "var(--ink-2)" }}>{c.d}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 p-4" style={{ background: "var(--onyx)", color: "var(--ivory)", border: "1px solid rgba(212,175,55,0.35)" }}>
        <div className="grid grid-cols-12 gap-5 items-center">
          <div className="col-span-7">
            <div className="overline" style={{ color: "var(--champagne)", fontSize: 9 }}>Why this matters to investors</div>
            <div className="font-serif-display text-[18px] leading-tight mt-1" style={{ color: "var(--ivory)" }}>
              A founder who has been <em style={{ color: "var(--champagne)" }}>right early</em> once — and built a profitable business on it — is statistically more likely to be right early again.
            </div>
          </div>
          <div className="col-span-5 grid grid-cols-2 gap-2">
            {[
              { v: "2018", l: "Vision" },
              { v: "2020", l: "Validated" },
              { v: "₹1Cr+", l: "FY20-26" },
              { v: "AI-led", l: "2026 ops" },
            ].map((m) => (
              <div key={m.l} className="p-2" style={{ border: "1px solid rgba(247,245,240,0.15)" }}>
                <div className="metric-value text-[18px]" style={{ color: "var(--champagne)" }}>{m.v}</div>
                <div className="overline mt-0.5" style={{ fontSize: 8, color: "rgba(247,245,240,0.6)" }}>{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Slide>
  );
}

// ─────────── Main Presentation component ───────────
const SLIDES = [SlideHero, SlideFounder, SlideJourney, SlideValuation, SlideHighlights, SlideRoadmap, SlideRisk, SlideClose];

export default function Presentation() {
  const [params] = useSearchParams();
  const isPdf = params.get("pdf") === "1";
  const [idx, setIdx] = useState(0);
  const total = SLIDES.length;

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    if (isPdf) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, isPdf]);

  if (isPdf) {
    // Render all slides stacked for PDF capture
    return (
      <div className="presentation-pdf" style={{ background: "var(--ivory)" }} data-testid="presentation-pdf">
        <style>{`
          @page { size: A4 landscape; margin: 0; }
          html, body { margin: 0; padding: 0; background: var(--ivory); }
          .print-slide { page-break-after: always; break-after: page; }
        `}</style>
        {SLIDES.map((S, i) => (
          <S key={i} number={i + 1} total={total} isPdf />
        ))}
      </div>
    );
  }

  const Slide = SLIDES[idx];
  return (
    <div className="min-h-screen flex flex-col items-center" style={{ background: "var(--ivory)" }} data-testid="presentation-page">
      {/* Top bar */}
      <div className="w-full px-6 py-4 flex items-center justify-between print-hide" style={{ background: "rgba(247,245,240,0.85)", backdropFilter: "blur(10px)", borderBottom: "1px solid rgba(26,26,26,0.08)" }}>
        <Link to="/admin" className="overline" style={{ color: "var(--bronze)" }} data-testid="back-admin-link">← Back to Admin</Link>
        <div className="overline" style={{ fontSize: 10 }}>Use ← / → arrow keys to navigate</div>
        <Link to="/" className="overline" style={{ color: "var(--bronze)" }} data-testid="view-site-link">View Full Brief →</Link>
      </div>

      {/* Slide */}
      <div className="flex-1 w-full flex items-center justify-center py-10">
        <Slide number={idx + 1} total={total} />
      </div>

      {/* Nav controls */}
      <div className="w-full px-6 py-4 flex items-center justify-center gap-4 print-hide" style={{ borderTop: "1px solid rgba(26,26,26,0.08)" }}>
        <button onClick={prev} disabled={idx === 0} className="btn-ghost" data-testid="prev-slide">
          <ChevronLeft size={14} /> Prev
        </button>
        <div className="flex gap-1.5">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`slide-dot-${i}`}
              style={{
                width: i === idx ? 24 : 8,
                height: 8,
                background: i === idx ? "var(--bronze)" : "rgba(26,26,26,0.2)",
                transition: "all 240ms ease",
                borderRadius: 999,
                border: 0,
                cursor: "pointer",
              }}
            />
          ))}
        </div>
        <button onClick={next} disabled={idx === total - 1} className="btn-ghost" data-testid="next-slide">
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
